import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	input,
	output,
	Pipe,
	PipeTransform,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { finalize, fromEvent, map, Observable, startWith, switchMap, takeUntil } from 'rxjs';

import { NodeType } from '@reach/interfaces/nodes/node-type.enum';

import { CommonButtonsModule } from '~ng-common-lib/buttons';
import { injectSrcConverter } from '~ng-common-lib/cdk/img';
import { ClientNode } from '~ng-common-lib/nodes/types/node';

// TODO: Refactor audio and video into `media`.

@Pipe({
	name: 'audioPreviewTimestamp',
	pure: true,
	standalone: true,
})
export class AudioPreviewTimestampPipe implements PipeTransform {
	transform(seconds: number): string {
		if (seconds === 0) {
			return '00:00';
		}

		// returns `hh:mm:ss` format (hh: is optional)
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const sec = Math.floor(seconds % 60);

		const hoursStr = hours.toString().padStart(2, '0');
		const minutesStr = minutes.toString().padStart(2, '0');
		const secStr = sec.toString().padStart(2, '0');

		return `${hours ? `${hoursStr}:` : ''}${minutesStr}:${secStr}`;
	}
}

@Component({
	selector: 'audio-preview',
	templateUrl: './audio-preview.component.html',
	styleUrls: ['./audio-preview.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, CommonButtonsModule, AudioPreviewTimestampPipe],
	host: {
		class: 'block',
	},
})
export class AudioPreviewComponent {
	private readonly srcConverter = injectSrcConverter();

	public readonly $file = input.required<ClientNode<NodeType.FILE>>({ alias: 'node' });

	public readonly error$$ = output({ alias: 'error' });

	protected readonly $audio = viewChild<unknown, ElementRef<HTMLAudioElement>>('audio', {
		read: ElementRef<HTMLElement>,
	});

	protected readonly $timerBar = viewChild<unknown, ElementRef<HTMLDivElement>>('timerBar', {
		read: ElementRef<HTMLElement>,
	});

	protected readonly $volumeBar = viewChild<unknown, ElementRef<HTMLDivElement>>('volumeBar', {
		read: ElementRef<HTMLElement>,
	});

	protected readonly $playing = signal(false);
	protected readonly $currentTime = signal(0);
	protected readonly $maxTime = signal(0);
	protected readonly $volume = signal(1);
	protected readonly $muted = signal(false);
	protected readonly $draggingVolume = signal(false);
	protected readonly $draggingTime = signal(false);

	protected readonly $volumeToShow = computed(() => {
		const volume = this.$volume();
		const muted = this.$muted();

		return (muted ? 0 : volume) * 100;
	});

	protected readonly $fileUrl = computed(() => {
		const file = this.$file();
		const url = file.data.previewUrl ?? file.data.fileUrl;
		return url ? this.srcConverter(url) : url;
	});

	constructor() {
		effect((cleanUp) => {
			const bar = this.$timerBar();
			const maxTime = this.$maxTime();

			untracked(() => {
				if (bar) {
					const sub = this.percentageMoveInElement(bar.nativeElement, () => {
						this.$draggingTime.set(false);
					}).subscribe({
						next: (percent) => {
							this.$draggingTime.set(true);
							const audio = this.$audio();
							if (audio) {
								audio.nativeElement.currentTime = percent * maxTime;
							}
						},
					});

					cleanUp(() => {
						sub.unsubscribe();
					});
				}
			});
		});

		effect((cleanUp) => {
			const bar = this.$volumeBar();

			untracked(() => {
				if (bar) {
					const sub = this.percentageMoveInElement(bar.nativeElement, () => {
						this.$draggingVolume.set(false);
					}).subscribe({
						next: (volume) => {
							this.$draggingVolume.set(true);
							const audio = this.$audio();
							if (audio) {
								audio.nativeElement.volume = volume;
								audio.nativeElement.muted = false;
							}
						},
					});

					cleanUp(() => {
						sub.unsubscribe();
					});
				}
			});
		});
	}

	public togglePlay(): void {
		this.$audio()?.nativeElement.paused
			? this.$audio()?.nativeElement.play()
			: this.$audio()?.nativeElement.pause();
	}

	public toggleMute(): void {
		const audio = this.$audio();

		if (audio) {
			const nextMuted = !audio.nativeElement.muted;
			audio.nativeElement.muted = nextMuted;

			if (!nextMuted) {
				const volume = audio.nativeElement.volume;

				if (volume === 0) {
					audio.nativeElement.volume = 1;
				}
			}
		}
	}

	public goBackwards(): void {
		const audio = this.$audio();

		if (audio) {
			audio.nativeElement.currentTime -= 10;
		}
	}

	public goForward(): void {
		const audio = this.$audio();

		if (audio) {
			audio.nativeElement.currentTime += 10;
		}
	}

	public onPause(): void {
		this.$playing.set(false);
	}

	public onPlay(): void {
		this.$playing.set(true);
	}

	public onEnded(): void {
		this.$playing.set(false);
	}

	public onError(): void {
		this.$playing.set(false);
		this.error$$.emit();
	}

	public onLoadedMetadata(): void {}

	public onDurationChange(): void {
		this.$maxTime.set(this.$audio()?.nativeElement.duration ?? 0);
	}

	public onTimeUpdate(): void {
		this.$currentTime.set(this.$audio()?.nativeElement.currentTime ?? 0);
	}

	public onVolumeChange(): void {
		this.$volume.set(this.$audio()?.nativeElement.volume ?? 1);
		this.$muted.set(this.$audio()?.nativeElement.muted ?? false);
	}

	/**
	 * Returns between 0 and 1
	 */
	private percentageMoveInElement(
		element: HTMLElement,
		onComplete: () => void
	): Observable<number> {
		return fromEvent<MouseEvent>(element, 'mousedown').pipe(
			switchMap((mousedownEvent) => {
				const mouseMove = fromEvent<MouseEvent>(document, 'mousemove');

				const mouseUp = fromEvent(document, 'mouseup');

				return mouseMove.pipe(
					startWith(mousedownEvent),
					map((mouseMoveEvent) => {
						mouseMoveEvent.preventDefault();
						mouseMoveEvent.stopPropagation();

						const { width, left } = element.getBoundingClientRect();
						const percent = (mouseMoveEvent.clientX - left) / width;

						return Math.max(0, Math.min(1, percent));
					}),
					takeUntil(mouseUp),
					finalize(onComplete)
				);
			})
		);
	}
}
